<template>
  <div v-if="importData">
    <div class="d-flex align-center mb-3">
      <h3 class="input-text-size">Validation Results</h3>
      <v-spacer />
      <v-btn
        text
        small
        @click="$emit('close')"
      >
        Close
      </v-btn>
    </div>

    <!-- Side-by-side layout: Table on left, Summary on right (wraps on small screens) -->
    <div class="d-flex flex-wrap" style="gap: 16px;">
      <!-- Left side: Table and filters -->
      <div style="flex: 1; min-width: 500px;">
        <!-- Filter Checkboxes -->
        <v-card outlined class="mb-3">
          <v-card-text class="py-2">
            <div class="d-flex flex-wrap" style="gap: 16px;">
              <v-checkbox
                v-model="showValid"
                label="Valid"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="showNotFound"
                label="Not Found"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="showOverpayment"
                label="Overpayment"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="showUnderpayment"
                label="Underpayment"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="showDuplicate"
                label="Duplicate"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="showPendingExists"
                label="Pending Payment"
                dense
                hide-details
                class="mt-0"
              />
              <!-- Not a validation status - narrows whatever the boxes above let through
                   to the records whose tax record carries an "Error on Mtg Co Payment"
                   status code. -->
              <v-checkbox
                v-model="showFlaggedOnly"
                label="Flagged per Status"
                dense
                hide-details
                class="mt-0"
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- Search box -->
        <v-text-field
          v-model="searchText"
          label="Search by Tax ID"
          outlined
          dense
          clearable
          class="mb-3"
        />

        <!-- Records Table -->
        <v-data-table
          :headers="headers"
          :items="filteredRecords"
          :search="searchText"
          :loading="loading"
          dense
          class="elevation-1"
        >
          <template #[`item.validationStatus`]="{ item }">
            <v-chip
              :color="getStatusColor(item.validationStatus)"
              small
              dark
            >
              {{ item.validationStatus }}
            </v-chip>
            <v-btn
              v-if="item.validationStatus === 'DUPLICATE'"
              x-small
              color="warning"
              class="ml-2"
              :loading="isRemovingDuplicate(item)"
              @click="handleRemoveDuplicate(item)"
            >
              Clear Status
            </v-btn>
          </template>
          <!-- Every status code carried by the matched tax record. -->
          <template #[`item.statusCode`]="{ item }">
            {{ (item.statusCode || []).join(', ') }}
          </template>
          <template #[`item.paymentAmount`]="{ item }">
            ${{ formatCurrency(item.paymentAmount) }}
          </template>
          <template #[`item.expectedAmount`]="{ item }">
            ${{ formatCurrency(item.expectedAmount) }}
          </template>
          <template #[`item.amountDifference`]="{ item }">
            <span
              :class="getDifferenceClass(item.amountDifference)"
            >
              {{ item.amountDifference > 0 ? '+' : '' }}${{ formatCurrency(item.amountDifference) }}
            </span>
          </template>
        </v-data-table>

        <!-- Download Buttons -->
        <div class="d-flex mt-3" style="gap: 8px;">
          <v-btn
            small
            outlined
            color="primary"
            @click="downloadErrorReport"
          >
            Download Error Report (CSV)
          </v-btn>
          <v-btn
            small
            outlined
            color="primary"
            @click="downloadErrorReportPdf"
          >
            Download Error Report (PDF)
          </v-btn>
          <v-btn
            small
            outlined
            color="primary"
            @click="downloadAllRecords"
          >
            Download All Records
          </v-btn>
        </div>
      </div>

      <!-- Right side: Summary Panel (compact) -->
      <div class="summary-panel">
        <v-card outlined>
          <v-card-title class="text-caption py-1 px-2">
            Summary
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-2">
            <div class="summary-row">
              <span class="summary-label">Records:</span>
              <span class="summary-value">{{ summary.totalRecords }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Submitted:</span>
              <span class="summary-value">${{ formatCurrency(summary.totalAmount) }}</span>
            </div>
            <v-divider class="my-1" />
            <div class="summary-row">
              <span class="summary-label success--text">Valid:</span>
              <span class="summary-value success--text">
                {{ summary.validCount }} / ${{ formatCurrency(summary.validAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label grey--text">Not Found:</span>
              <span class="summary-value grey--text">
                {{ summary.notFoundCount }} / ${{ formatCurrency(summary.notFoundAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label orange--text">Overpayment:</span>
              <span class="summary-value orange--text">
                {{ summary.overpaymentCount }} / +${{ formatCurrency(summary.overpaymentSum) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label red--text">Underpayment:</span>
              <span class="summary-value red--text">
                {{ summary.underpaymentCount }} / -${{ formatCurrency(summary.underpaymentSum) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label purple--text">Duplicate:</span>
              <span class="summary-value purple--text">
                {{ summary.duplicateCount }} / ${{ formatCurrency(summary.duplicateAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label blue--text">Pending Pmt:</span>
              <span class="summary-value blue--text">
                {{ summary.pendingExistsCount }} /
                ${{ formatCurrency(summary.pendingExistsAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label red--text">Flagged:</span>
              <span class="summary-value red--text">
                {{ summary.flaggedCount }} / ${{ formatCurrency(summary.flaggedAmount) }}
              </span>
            </div>
            <v-divider class="my-1" />
            <div class="summary-row font-weight-bold">
              <span class="summary-label">Refund:</span>
              <span class="summary-value">${{ formatCurrency(summary.refundAmount) }}</span>
            </div>
            <div class="summary-row font-weight-bold">
              <span class="summary-label">Paid/Wire:</span>
              <span class="summary-value">${{ formatCurrency(summary.paidAmount) }}</span>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- Payment Footer (Full Width Below Two-Column Layout) -->
    <v-card outlined class="mt-3">
      <v-card-title class="text-subtitle-1 py-2">
        Payment
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-3">
        <div class="d-flex flex-wrap" style="gap: 16px;">
          <!-- Left Column: Payment Options or Payment Created Status -->
          <div style="flex: 1; min-width: 200px;">
            <template v-if="!createdPaymentId && !importData.paymentApplied">
              <div class="text-caption font-weight-bold mb-2">
                Payment Options
              </div>
              <v-checkbox
                v-model="postOverPayments"
                label="Post Over Payments"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="postPartials"
                label="Post Partials"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="postDuplicates"
                label="Post Duplicates"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="postPendingPayments"
                label="Post Pending Payments"
                dense
                hide-details
                class="mt-0"
              />
            </template>
            <template v-else>
              <div class="text-h6 success--text mb-2">
                Payment Created
              </div>
              <div class="text-caption mb-1">
                <strong>Payment Number:</strong> {{ createdPaymentNumber || 'N/A' }}
              </div>
              <div class="text-caption mb-2">
                <strong>Fiscal Year:</strong> {{ createdPaymentFiscalYear || 'N/A' }}
              </div>
              <v-btn
                small
                color="primary"
                :disabled="!createdPaymentNumber"
                @click="handleViewPayment"
              >
                View Payment
              </v-btn>
            </template>
          </div>

          <!-- Middle Column: Payment Type and Details -->
          <div style="flex: 1; min-width: 250px;">
            <template v-if="!createdPaymentId && !importData.paymentApplied">
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model="selectedPaymentType"
                    :items="paymentTypeOptions"
                    item-text="text"
                    item-value="value"
                    label="Payment Type"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                </v-col>
                <v-col cols="6">
                  <!-- Conditional field based on payment type -->
                  <v-text-field
                    v-if="selectedPaymentType === 'CHECK'"
                    v-model="checkNumber"
                    label="Check Number"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                  <v-select
                    v-else-if="selectedPaymentType === 'EFT'"
                    v-model="eftType"
                    :items="eftTypeOptions"
                    item-text="text"
                    item-value="value"
                    label="EFT Type"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                  <v-select
                    v-else-if="selectedPaymentType === 'CREDIT'"
                    v-model="creditType"
                    :items="creditTypeOptions"
                    item-text="text"
                    item-value="value"
                    label="Card Type"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                </v-col>
              </v-row>
              <v-text-field
                v-model="paidBy"
                label="Paid By (optional)"
                dense
                outlined
                hide-details
                class="mb-2"
                v-uppercase="isEverythingToCaps"
              />
            </template>
          </div>

          <!-- Right Column: Payment Creation (only shown if payment not yet created) -->
          <div style="flex: 1; min-width: 200px;">
            <template v-if="!createdPaymentId && !importData.paymentApplied">
              <v-text-field
                v-model="postingDate"
                label="Posting Date"
                dense
                outlined
                readonly
                hide-details
                class="mb-2"
              />
              <v-select
                v-model="selectedWorkstation"
                :items="paymentWkstNumList"
                label="Workstation"
                dense
                outlined
                hide-details
                class="mb-2"
              />
              <v-btn
                small
                color="primary"
                :disabled="!postingDate || !selectedWorkstation || creatingPayment"
                :loading="creatingPayment"
                @click="handleCreatePayment"
              >
                Create Payment
              </v-btn>

              <!-- Loading/Status Messages -->
              <div v-if="statusMessage" class="mt-3 text-caption">
                {{ statusMessage }}
              </div>
            </template>
          </div>

          <!-- Right Column: Update Tax Records (always visible) -->
          <div style="flex: 1; min-width: 200px;">
            <div class="text-caption font-weight-bold mb-2">
              Update Mortgage Codes on Tax Roll Records:
            </div>
            <v-select
              v-model="selectedMtgCode"
              :items="mortgageCompanyOptions"
              label="Mortgage Code (Optional)"
              dense
              outlined
              clearable
              hide-details
              class="mb-2"
            />
            <v-radio-group
              v-model="mtgUpdateMode"
              dense
              hide-details
              class="mt-0 mb-2"
            >
              <v-radio
                label="Use codes from file"
                value="fromFile"
                class="mb-1"
              />
              <v-radio
                label="Apply selected code to all (override)"
                value="overrideAll"
              />
            </v-radio-group>
            <v-btn
              small
              color="primary"
              :disabled="updatingTaxRecords || (mtgUpdateMode !== 'fromFile' && !selectedMtgCode)"
              :loading="updatingTaxRecords"
              @click="handleUpdateTaxRecords"
            >
              Update Tax Records
            </v-btn>

            <!-- Missing Mortgage Codes Alert -->
            <v-alert
              v-if="missingMtgCodes.length > 0"
              type="warning"
              dense
              outlined
              class="mt-3 mb-0"
            >
              <div class="text-caption font-weight-bold mb-1">
                Codes in file but not in system:
              </div>
              <div class="text-caption">
                <v-chip
                  v-for="code in missingMtgCodes"
                  :key="code"
                  x-small
                  class="mr-1 mb-1"
                >
                  {{ code }}
                </v-chip>
              </div>
            </v-alert>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Payment Dialog -->
    <CRUDDialog
      ref="paymentCrudDialog"
      :baseCrudKey="paymentDialogBaseCrudKey"
      :canSave="false"
      @upsertItem="onSavePayment"
    >
      <template #editedItem>
        <paymentDialog
          ref="paymentDialogRef"
          :baseCrudKey="paymentDialogBaseCrudKey"
          @callFromPayDialog="onSavePayment"
        />
      </template>
      <template #additionalActions>
        <UploadFile
          class="mtgtrans uploadFile"
          text
          :bucketName="attachmentConstants.bucketName"
          :program="attachmentConstants.program"
          :parentObjectType="attachmentConstants.parentObjectType"
          :parentObjectId="createdPaymentId || ''"
          :itemIsSaved="!!createdPaymentId"
        >
          <template v-slot:activator="{ activatorProps }">
            <v-btn
              :disabled="!createdPaymentId"
              class="input-text-size payment-transaction-list-upload-btn"
              id="paymentTransactionListUploadBtn"
              text
              color="blue darken-1"
              @click="activatorProps.leftClick"
            >
              {{ activatorProps.caption }}
            </v-btn>
          </template>
        </UploadFile>
        <AuditDialog
          v-if="createdPaymentId"
          :identifier="createdPaymentId"
          activatorCaption="Audit History"
          activatorCaptionStyle="input-text-size"
        />
      </template>
      <template #additionalActionsAfterSave>
        <v-btn
          class="input-text-size payment-transaction-list-save-btn"
          id="paymentTransactionListSaveBtn"
          text
          color="blue darken-1"
          @click="onSavePayment(false)"
        >
          Save
        </v-btn>
        <v-btn
          class="input-text-size payment-transaction-list-save-print-btn"
          id="paymentTransactionListSavePrintBtn"
          text
          color="blue darken-1"
          @click="onSavePayment(true)"
        >
          Save & print
        </v-btn>
      </template>
    </CRUDDialog>
  </div>
</template>

<script>
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import CRUDDialog from '../../common/base/crud-2.0/CRUDDialog.vue';
import paymentDialog from '../payments/paymenteditDialog/paymentDialog.vue';
import UploadFile from '../../common/UploadFile.vue';
import AuditDialog from '../../common/audit/AuditDialog.vue';
import { clients } from '../../../util/clients';
import { generateQueryString } from '../../common/base/report/report-util';
import { toStandardDate } from '../../../util/shared/tmc-global';
import GlobalToCaps from '../../../util/shared/GlobalToCaps.mixin.vue';

export default {
  name: 'MortgagePaymentRecordsTable',
  mixins: [GlobalToCaps],
  components: {
    CRUDDialog,
    paymentDialog,
    UploadFile,
    AuditDialog,
  },
  props: {
    importData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      showValid: true,
      showNotFound: true,
      showOverpayment: true,
      showUnderpayment: true,
      showDuplicate: true,
      showPendingExists: true,
      showFlaggedOnly: false,
      searchText: '',
      // Payment creation fields
      postOverPayments: true,
      postPartials: true,
      postDuplicates: false,
      // Off by default, like Post Duplicates: the parcel already has an unposted payment
      // against it, so paying it again has to be a deliberate choice.
      postPendingPayments: false,
      selectedWorkstation: null,
      // Tender type fields
      selectedPaymentType: 'EFT',
      checkNumber: '',
      eftType: 'OTHER',
      creditType: '',
      paidBy: '',
      paymentTypeOptions: [
        { value: 'EFT', text: 'EFT' },
        { value: 'CHECK', text: 'Check' },
        { value: 'CASH', text: 'Cash' },
        { value: 'CREDIT', text: 'Credit Card' },
        { value: 'OTHER', text: 'Other' },
      ],
      eftTypeOptions: [
        { value: 'SIMPLIFILE', text: 'Simplifile' },
        { value: 'OTC', text: 'OTC' },
        { value: 'IRS', text: 'IRS' },
        { value: 'FORTE', text: 'Forte' },
        { value: 'CSC', text: 'CSC' },
        { value: 'OTHER', text: 'Other' },
      ],
      creditTypeOptions: [
        { value: 'VISA', text: 'Visa' },
        { value: 'MC', text: 'MasterCard' },
        { value: 'AMEX', text: 'American Express' },
        { value: 'DISC', text: 'Discover' },
        { value: 'OTHER', text: 'Other' },
      ],
      creatingPayment: false,
      updatingTaxRecords: false,
      statusMessage: '',
      createdPaymentId: null,
      createdPaymentNumber: null,
      createdPaymentFiscalYear: null,
      pollingInterval: null,
      // Mortgage code update fields
      selectedMtgCode: null,
      mtgUpdateMode: 'fromFile',
      mortgageCompanyOptions: [],
      // Remove duplicate tracking
      removingDuplicateIndex: null,
      // Payment dialog
      paymentDialogBaseCrudKey: 'mortgagePaymentDialog',
      // Status codes marked "Error on Mtg Co Payment" on the shared status code list
      flaggedCodesBaseCrudKey: 'mortgagePaymentFlaggedStatusCodes',
      // Attachment constants for upload file
      attachmentConstants: {
        bucketName: 'taxPayment',
        program: 'taxPayment',
        parentObjectType: 'taxPayments',
      },
      headers: [
        {
          text: 'Tax Year', value: 'taxYear', width: '100px', sortable: true,
        },
        {
          text: 'Tax ID', value: 'taxId', width: '100px', sortable: true,
        },
        {
          text: 'Property ID', value: 'propertyOrParcelId', width: '120px', sortable: true,
        },
        {
          text: 'Mtg Code', value: 'mtgCode', width: '120px', sortable: true,
        },
        {
          text: 'Bank Name', value: 'nameFromMtgComp', sortable: true,
        },
        {
          text: 'Payment', value: 'paymentAmount', width: '110px', sortable: true,
        },
        {
          text: 'Expected', value: 'expectedAmount', width: '110px', sortable: true,
        },
        {
          text: 'Difference', value: 'amountDifference', width: '120px', sortable: true,
        },
        {
          text: 'Status', value: 'validationStatus', width: '200px', sortable: true,
        },
        {
          text: 'Code', value: 'statusCode', width: '90px', sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      paymentWkstNumList: (state) => state.tax.taxPayment.paymentWkstNumList || [],
    }),
    ...mapGetters('base/crud', ['item', 'items']),
    // Resolved here rather than baked into the import, so that turning "Error on Mtg Co
    // Payment" on or off re-flags imports that already ran.
    flaggedStatusCodeSet() {
      const codes = this.items(this.flaggedCodesBaseCrudKey) || [];
      return new Set(codes.map((code) => Number(code.statusCode)));
    },
    missingMtgCodes() {
      // Find mortgage codes in the file that don't exist in the system
      if (!this.importData || !this.importData.records) return [];

      // Get unique codes from the file
      const codesInFile = [...new Set(
        this.importData.records
          .filter((record) => record.mtgCode)
          .map((record) => record.mtgCode),
      )];

      // Get codes that exist in the system
      const codesInSystem = this.mortgageCompanyOptions.map((opt) => opt.value);

      // Return codes that are in file but not in system
      return codesInFile.filter((code) => !codesInSystem.includes(code));
    },
    postingDate() {
      // Get posting date from importData
      if (this.importData && this.importData.postingDate) {
        return toStandardDate(this.importData.postingDate);
      }
      return '';
    },
    filteredRecords() {
      if (!this.importData || !this.importData.records) {
        return [];
      }

      return this.importData.records.filter((record) => {
        if (this.showFlaggedOnly && !this.isFlagged(record)) return false;
        if (record.validationStatus === 'VALID' && !this.showValid) return false;
        if (record.validationStatus === 'NOT_FOUND' && !this.showNotFound) return false;
        if (record.validationStatus === 'OVERPAYMENT' && !this.showOverpayment) return false;
        if (record.validationStatus === 'UNDERPAYMENT' && !this.showUnderpayment) return false;
        if (record.validationStatus === 'DUPLICATE' && !this.showDuplicate) return false;
        if (record.validationStatus === 'PENDING_EXISTS' && !this.showPendingExists) return false;
        return true;
      });
    },
    summary() {
      if (!this.importData || !this.importData.records) {
        return {
          totalRecords: 0,
          totalAmount: 0,
          validCount: 0,
          validAmount: 0,
          notFoundCount: 0,
          notFoundAmount: 0,
          overpaymentCount: 0,
          overpaymentSum: 0,
          underpaymentCount: 0,
          underpaymentSum: 0,
          duplicateCount: 0,
          duplicateAmount: 0,
          pendingExistsCount: 0,
          pendingExistsAmount: 0,
          flaggedCount: 0,
          flaggedAmount: 0,
          refundAmount: 0,
          paidAmount: 0,
        };
      }

      const { records } = this.importData;
      const totalRecords = records.length;
      const totalAmount = records.reduce((sum, r) => sum + (r.paymentAmount || 0), 0);

      const validRecords = records.filter((r) => r.validationStatus === 'VALID');
      const validCount = validRecords.length;
      const validAmount = validRecords.reduce((sum, r) => sum + (r.paymentAmount || 0), 0);

      const notFoundRecords = records.filter((r) => r.validationStatus === 'NOT_FOUND');
      const notFoundCount = notFoundRecords.length;
      const notFoundAmount = notFoundRecords.reduce((sum, r) => sum + (r.paymentAmount || 0), 0);

      const overpaymentRecords = records.filter((r) => r.validationStatus === 'OVERPAYMENT');
      const overpaymentCount = overpaymentRecords.length;
      const overpaymentSum = overpaymentRecords.reduce(
        (sum, r) => sum + (r.amountDifference || 0),
        0,
      );

      const underpaymentRecords = records.filter((r) => r.validationStatus === 'UNDERPAYMENT');
      const underpaymentCount = underpaymentRecords.length;
      const underpaymentSum = underpaymentRecords.reduce(
        (sum, r) => sum + Math.abs(r.amountDifference || 0),
        0,
      );

      const duplicateRecords = records.filter((r) => r.validationStatus === 'DUPLICATE');
      const duplicateCount = duplicateRecords.length;
      const duplicateAmount = duplicateRecords.reduce(
        (sum, r) => sum + (r.paymentAmount || 0),
        0,
      );

      const pendingExistsRecords = records.filter((r) => r.validationStatus === 'PENDING_EXISTS');
      const pendingExistsCount = pendingExistsRecords.length;
      const pendingExistsAmount = pendingExistsRecords.reduce(
        (sum, r) => sum + (r.paymentAmount || 0),
        0,
      );

      // Flagged records cut across the statuses above, so this count is not part of the
      // refund/paid arithmetic - it is a review total only.
      const flaggedRecordsList = records.filter((r) => this.isFlagged(r));
      const flaggedCount = flaggedRecordsList.length;
      const flaggedAmount = flaggedRecordsList.reduce((sum, r) => sum + (r.paymentAmount || 0), 0);

      // Refund Amount = Not Found Amount + Overpayment Sum + Duplicate Amount + Pending Exists
      const refundAmount = notFoundAmount + overpaymentSum + duplicateAmount + pendingExistsAmount;

      // Paid/Wire Amount = Total Amount Submitted - Refund Amount
      const paidAmount = totalAmount - refundAmount;

      return {
        totalRecords,
        totalAmount,
        validCount,
        validAmount,
        notFoundCount,
        notFoundAmount,
        overpaymentCount,
        overpaymentSum,
        underpaymentCount,
        underpaymentSum,
        duplicateCount,
        duplicateAmount,
        pendingExistsCount,
        pendingExistsAmount,
        flaggedCount,
        flaggedAmount,
        refundAmount,
        paidAmount,
      };
    },
  },
  created() {
    // Load workstation list
    this.loadPaymentsWkstList();
    // Load mortgage company options
    this.loadMortgageCompanies();
    // Load the status codes that flag a record in the validation results
    this.loadFlaggedStatusCodes();
  },
  beforeDestroy() {
    // Clean up polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  methods: {
    ...mapMutations('base/crud', ['setItem', 'setIsVisible']),
    ...mapActions('tax/taxPayment', [
      'loadPaymentsWkstList',
      'loadPayment',
      'upsertPayment',
    ]),
    ...mapActions('mortgagePaymentImport', [
      'createPaymentFromImport',
      'updateTaxRecords',
      'updateRecordValidationStatus',
    ]),
    ...mapActions('shared/taxMortgageCodesShared', ['loadTaxMortgageCodesSharedRecords']),
    ...mapActions('shared/taxStatusCodesShared', ['loadTaxStatusCodesSharedRecords']),
    ...mapActions(['reAuth']),
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00';
      return Math.abs(value).toFixed(2);
    },
    getStatusColor(status) {
      const colors = {
        VALID: 'green',
        OVERPAYMENT: 'orange',
        UNDERPAYMENT: 'red',
        NOT_FOUND: 'grey',
        DUPLICATE: 'purple',
        PENDING_EXISTS: 'blue',
      };
      return colors[status] || 'grey';
    },
    getDifferenceClass(difference) {
      if (Math.abs(difference) < 0.01) return '';
      if (difference > 0) return 'orange--text';
      return 'red--text';
    },
    // The record's tax status codes that the county marked "Error on Mtg Co Payment".
    // Flagging is independent of validationStatus - a record can be VALID and flagged.
    flaggedCodesFor(record) {
      if (!this.flaggedStatusCodeSet.size || !record.statusCode) return [];
      return record.statusCode.filter((code) => this.flaggedStatusCodeSet.has(Number(code)));
    },
    isFlagged(record) {
      return this.flaggedCodesFor(record).length > 0;
    },
    async loadFlaggedStatusCodes() {
      await this.loadTaxStatusCodesSharedRecords({
        baseCrudKey: this.flaggedCodesBaseCrudKey,
        criteria: {
          errorOnMtgCoPayment: true,
          limit: 1000,
          skip: 0,
          sortBy: [{ column: 'statusCode', direction: 'asc' }],
        },
      });
    },
    isRemovingDuplicate(item) {
      if (this.removingDuplicateIndex === null) {
        return false;
      }
      return item.originalIndex === this.removingDuplicateIndex;
    },
    downloadErrorReport() {
      if (!this.importData || !this.importData.records) return;

      // Filter only records with errors
      const errorRecords = this.importData.records.filter(
        (r) => r.validationStatus !== 'VALID',
      );

      if (errorRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No error records to download');
        return;
      }

      this.downloadCSV(errorRecords, 'error-report.csv');
    },
    async downloadErrorReportPdf() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      // Check if there are error records
      const errorRecords = this.importData.records.filter(
        (r) => r.validationStatus !== 'VALID',
      );

      if (errorRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No error records to download');
        return;
      }

      const { jwt } = await this.reAuth();
      const { backendRest } = clients.direct;
      const params = [
        `fileQueueId=${this.importData.fileQueueId}`,
        `token=${jwt}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/mortgageCoProcessing/printPaymentImportErrorList${queryString}`;
      window.open(url, '_blank');
    },
    downloadAllRecords() {
      if (!this.importData || !this.importData.records) return;
      this.downloadCSV(this.importData.records, 'all-records.csv');
    },
    downloadCSV(records, filename) {
      // Create CSV content
      const headers = [
        'Tax Year',
        'Tax ID',
        'Property ID',
        'Mtg Code',
        'Bank Name',
        'Payment Amount',
        'Expected Amount',
        'Difference',
        'Status',
        'Code',
      ];
      const csvRows = [headers.join(',')];

      records.forEach((record) => {
        const row = [
          record.taxYear || '',
          record.taxId || '',
          record.propertyOrParcelId || '',
          record.mtgCode || '',
          `"${(record.nameFromMtgComp || '').replace(/"/g, '""')}"`,
          record.paymentAmount || 0,
          record.expectedAmount || 0,
          record.amountDifference || 0,
          record.validationStatus || '',
          `"${(record.statusCode || []).join(' ')}"`,
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async handleCreatePayment() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      this.creatingPayment = true;
      this.statusMessage = 'Creating payment...';

      try {
        await this.createPaymentFromImport({
          fileQueueId: this.importData.fileQueueId,
          workstation: this.selectedWorkstation,
          applyOverPayments: this.postOverPayments,
          skipPartials: !this.postPartials,
          skipDuplicates: !this.postDuplicates,
          skipPending: !this.postPendingPayments,
          // Tender type fields
          paymentType: this.selectedPaymentType.toLowerCase(),
          checkNumber: this.selectedPaymentType === 'CHECK' ? this.checkNumber : '',
          eftType: this.selectedPaymentType === 'EFT' ? this.eftType.toLowerCase() : '',
          creditType: this.selectedPaymentType === 'CREDIT' ? this.creditType.toLowerCase() : '',
          paidBy: this.paidBy,
        });

        // Start polling for status
        this.statusMessage = 'Payment processing...';
        this.startPolling('payment');
      } catch (error) {
        this.creatingPayment = false;
        this.statusMessage = '';
        // eslint-disable-next-line no-console
        console.error('Error creating payment:', error);
        // eslint-disable-next-line no-alert
        alert(`Error creating payment: ${error.message}`);
      }
    },
    async handleRemoveDuplicate(item) {
      if (!this.importData || !this.importData.fileQueueId || !this.importData.records) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      // Use originalIndex which was added when loading data
      const { originalIndex, paymentAmount, expectedAmount } = item;

      if (originalIndex === undefined) {
        // eslint-disable-next-line no-alert
        alert('Record index not found');
        return;
      }

      this.removingDuplicateIndex = originalIndex;

      // Determine the appropriate status based on payment comparison
      let newStatus = 'VALID';
      if (paymentAmount !== undefined && expectedAmount !== undefined) {
        const difference = paymentAmount - expectedAmount;
        if (Math.abs(difference) >= 0.01) {
          newStatus = difference > 0 ? 'OVERPAYMENT' : 'UNDERPAYMENT';
        }
      }

      try {
        const result = await this.updateRecordValidationStatus({
          fileQueueId: this.importData.fileQueueId,
          recordIndex: originalIndex,
          validationStatus: newStatus,
        });

        if (result && result.success) {
          // Emit event to parent to refresh data and update the record
          this.$emit('update-record-status', { recordIndex: originalIndex, newStatus });
        } else {
          // eslint-disable-next-line no-alert
          alert(result.message || 'Failed to update record status');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error removing duplicate status:', error);
        // eslint-disable-next-line no-alert
        alert(`Error removing duplicate status: ${error.message}`);
      } finally {
        this.removingDuplicateIndex = null;
      }
    },
    async loadMortgageCompanies() {
      try {
        const criteria = {
          limit: 1000,
          skip: 0,
          sortBy: [{
            column: 'mtgCode',
            direction: 'asc',
          }],
        };
        const codes = await this.loadTaxMortgageCodesSharedRecords({ criteria });
        if (codes && codes.length > 0) {
          this.mortgageCompanyOptions = codes.map((item) => ({
            text: item.mtgCoName ? `${item.mtgCode} - ${item.mtgCoName}` : item.mtgCode,
            value: item.mtgCode,
          }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading mortgage codes:', error);
      }
    },
    async handleUpdateTaxRecords() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      this.updatingTaxRecords = true;
      this.statusMessage = 'Updating tax records...';

      try {
        await this.updateTaxRecords({
          fileQueueId: this.importData.fileQueueId,
          mtgCode: this.selectedMtgCode,
          updateMode: this.mtgUpdateMode,
        });

        // Start polling for status
        this.startPolling('taxRecords');
      } catch (error) {
        this.updatingTaxRecords = false;
        this.statusMessage = '';
        // eslint-disable-next-line no-console
        console.error('Error updating tax records:', error);
        // eslint-disable-next-line no-alert
        alert(`Error updating tax records: ${error.message}`);
      }
    },
    async startPolling(type) {
      // Poll FileQueue status every 2 seconds
      let attempts = 0;
      const maxAttempts = 60; // 2 minutes timeout

      this.pollingInterval = setInterval(async () => {
        attempts += 1;

        if (attempts > maxAttempts) {
          clearInterval(this.pollingInterval);
          this.creatingPayment = false;
          this.updatingTaxRecords = false;
          this.statusMessage = 'Operation timed out. Please refresh the page.';
          return;
        }

        try {
          // Poll FileQueue status from backend
          const response = await this.$store.dispatch('mortgagePaymentImport/getFileQueueStatus', {
            fileQueueId: this.importData.fileQueueId,
          });

          if (response && (response.Status === 'Completed' || response.Status === 'Payment Created')) {
            clearInterval(this.pollingInterval);

            if (type === 'payment') {
              this.creatingPayment = false;
              this.statusMessage = '';

              // Extract payment details from Parameters
              if (response.Parameters && response.Parameters.paymentId) {
                this.createdPaymentId = response.Parameters.paymentId;
                this.createdPaymentNumber = response.Parameters.paymentNumber;
                this.createdPaymentFiscalYear = response.Parameters.fiscalYear;

                // Refresh the import data to show paymentApplied status
                this.$emit('refresh');

                // eslint-disable-next-line no-alert
                alert('Payment created successfully!');
              } else {
                // eslint-disable-next-line no-alert
                alert('Payment creation completed but details not found.');
              }
            } else if (type === 'taxRecords') {
              this.updatingTaxRecords = false;
              this.statusMessage = '';

              // Refresh the import data
              this.$emit('refresh');

              // eslint-disable-next-line no-alert
              alert('Tax records updated successfully!');
            }
          } else if (response && response.Status === 'Error') {
            clearInterval(this.pollingInterval);
            this.creatingPayment = false;
            this.updatingTaxRecords = false;
            this.statusMessage = '';

            const errorMsg = response.ErrorDetail && response.ErrorDetail[0]
              ? response.ErrorDetail[0].ErrorDescription
              : 'Unknown error occurred';
            // eslint-disable-next-line no-alert
            alert(`Error: ${errorMsg}`);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error polling FileQueue:', error);
        }
      }, 2000);
    },
    async handleViewPayment() {
      if (!this.createdPaymentNumber || !this.createdPaymentFiscalYear) {
        // eslint-disable-next-line no-alert
        alert('Payment information not available');
        return;
      }

      try {
        // Load the payment using payment number and fiscal year
        const payments = await this.loadPayment({
          baseCrudKey: this.paymentDialogBaseCrudKey,
          criteria: {
            paymentNumber: this.createdPaymentNumber,
            fiscalYear: this.createdPaymentFiscalYear,
          },
        });

        if (payments && payments.length > 0) {
          // Set the payment item in the store
          this.setItem([this.paymentDialogBaseCrudKey, payments[0]]);
          // Open the dialog
          this.setIsVisible([this.paymentDialogBaseCrudKey, true]);
        } else {
          // eslint-disable-next-line no-alert
          alert('Payment not found');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading payment:', error);
        // eslint-disable-next-line no-alert
        alert('Error loading payment details');
      }
    },
    async onSavePayment(shouldPrint = false) {
      try {
        const payment = this.item(this.paymentDialogBaseCrudKey);
        await this.upsertPayment({
          baseCrudKey: this.paymentDialogBaseCrudKey,
          item: payment,
          shouldPrint,
        });
        // Close the dialog after save
        this.setIsVisible([this.paymentDialogBaseCrudKey, false]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving payment:', error);
        // eslint-disable-next-line no-alert
        alert('Error saving payment');
      }
    },
    async loadPaymentDetailsIfExists() {
      // If payment has been applied, load the payment details from FileQueue
      if (this.importData && this.importData.paymentApplied && this.importData.fileQueueId) {
        try {
          const response = await this.$store.dispatch(
            'mortgagePaymentImport/getFileQueueStatus',
            {
              fileQueueId: this.importData.fileQueueId,
            },
          );

          if (response && response.Parameters) {
            this.createdPaymentId = response.Parameters.paymentId || null;
            this.createdPaymentNumber = response.Parameters.paymentNumber || null;
            this.createdPaymentFiscalYear = response.Parameters.fiscalYear || null;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading payment details:', error);
        }
      }
    },
  },
  watch: {
    importData: {
      immediate: true,
      handler() {
        this.loadPaymentDetailsIfExists();
      },
    },
  },
};
</script>

<style scoped>
.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}
.summary-label {
  font-size: 0.875rem;
}
.summary-value {
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
